import env from '../../env.js';

// Profile, passkeys and the first-login welcome flow.
// Merged onto the web-dashboard prototype (see dashboard.js);
// `this` is the component instance.
export default {

    needsProfileSetup() {
      return !this.userName || this.userName === this.email;
    },

    async maybeShowWelcome() {
      if (!this.email) return;
      const seenKey = 'welcomed:' + this.email;
      if (sessionStorage.getItem(seenKey)) return;

      await this.loadCredentials();
      const needsProfile = this.needsProfileSetup();
      const needsPasskey = this.credentialsLoaded && this.credentials.length === 0;
      if (!needsProfile && !needsPasskey) return;

      sessionStorage.setItem(seenKey, '1');
      if (needsProfile) {
        // Don't present the email placeholder as if it were a name.
        if (this.userName === this.email) this.userName = '';
        if (this.userDisplayName === this.email) this.userDisplayName = '';
        this.welcomeStep = 1;
      } else {
        this.welcomeStep = 2;
      }
      this.update();
      this.querySelector('.welcome-dialog').showModal();
    },

    async welcomeSaveProfile() {
      if (!this.userName.trim()) {
        this.showToast('Please enter your name', 'danger');
        return;
      }
      this.welcomeLoading = true;
      this.update();
      try {
        const response = await this.apiFetch(`${env.apiUrl}profile`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: this.userName.trim(), display_name: this.userDisplayName.trim() })
        });
        const data = await response.json();
        if (!response.ok) {
          this.showToast(data, 'danger');
          return;
        }
        this.userName = data.name || '';
        this.userDisplayName = data.display_name || '';
        this.showToast('Profile saved');
        if (this.credentialsLoaded && this.credentials.length === 0) {
          this.welcomeStep = 2;
        } else {
          this.closeWelcome();
        }
      } catch (error) {
        this.showToast(error.message, 'danger');
      } finally {
        this.welcomeLoading = false;
      }
    },

    welcomeSkipProfile() {
      if (this.credentialsLoaded && this.credentials.length === 0) {
        this.welcomeStep = 2;
      } else {
        this.closeWelcome();
      }
    },

    async welcomeAddPasskey() {
      await this.registerPasskey();
      if (this.credentials.length > 0) {
        this.closeWelcome();
      }
    },

    closeWelcome() {
      this.welcomeStep = 0;
      this.querySelector('.welcome-dialog')?.close();
    },

    parseUserAgent(ua) {
      if (!ua) return 'Unknown';
      let os = 'Unknown';
      if (ua.includes('Macintosh')) os = 'macOS';
      else if (ua.includes('Windows')) os = 'Windows';
      else if (ua.includes('Android')) os = 'Android';
      else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
      else if (ua.includes('CrOS')) os = 'ChromeOS';
      else if (ua.includes('Linux')) os = 'Linux';

      let browser = 'Unknown';
      if (ua.includes('Edg/')) browser = 'Edge';
      else if (ua.includes('OPR/') || ua.includes('Opera')) browser = 'Opera';
      else if (ua.includes('Chrome/')) browser = 'Chrome';
      else if (ua.includes('Safari/')) browser = 'Safari';
      else if (ua.includes('Firefox/')) browser = 'Firefox';

      return browser + ' on ' + os;
    },

    aaguidName(aaguid) {
      return this.aaguids[aaguid]?.name || aaguid || '';
    },

    aaguidIcon(aaguid) {
      const info = this.aaguids[aaguid];
      if (!aaguid || !info || !info.icon_ext) return '';
      const dark = document.documentElement.getAttribute('data-theme') === 'dark';
      const suffix = dark ? '_dark' : '';
      return 'resources/aaguid_icons/' + aaguid + suffix + '.' + info.icon_ext;
    },

    // Which site a passkey was registered for: this console lists ALL of an
    // account's passkeys (the sub-site managers filter to their own RP), so
    // the relying-party domain is what tells two of them apart. Blank on rows
    // that predate the rp_id column — the server treats those as the portal's
    // own RP, but the row itself doesn't say so, so don't claim it here.
    credRP(cred) {
      return cred.rp_id || '—';
    },

    async loadUserData() {
      try {
        const response = await this.apiFetch(`${env.apiUrl}me`);

        if (response.ok) {
          const user = await response.json();
          this.email = user.email;
          this.userName = user.name || '';
          this.userDisplayName = user.display_name || '';
          this.isAdmin = !!user.is_admin;
          this.isRoot = !!user.is_root;
          this.phone = user.phone || '';
          this.phoneVerified = user.phone_verified || '';
          this.phoneOptedOut = !!(user.phone && !user.phone_consent);
          // Awaited, unlike before: the test-environment answer decides
          // whether anything else may open a dialog on top of it.
          await this.loadFeatures();
          if (!this.maybeShowTestEnv()) this.maybeShowWelcome();
        } else {
          sessionStorage.removeItem('session');
          sessionStorage.removeItem('testEnvAck');
          this.dispatchEvent(new CustomEvent('logout', { bubbles: true, composed: true }));
          return;
        }
      } catch (error) {
        // Network error during refresh — don't log out, keep current session
        return;
      }
    },

    // Fetched once, shared by the self-service passkeys page and the admin
    // per-user passkeys dialog.
    ensureAaguids() {
      if (Object.keys(this.aaguids).length) return;
      fetch('resources/aaguids.json').then(r => r.json()).then(data => {
        this.aaguids = data;
        this.update();
      });
    },

    async loadPasskeyData() {
      this.ensureAaguids();
      await this.loadCredentials();
    },

    async loadCredentials() {
      try {
        const response = await this.apiFetch(`${env.apiUrl}credentials`);

        if (response.ok) {
          const data = await response.json();
          this.credentials = data || [];
          this.credentialsLoaded = true;
        }
      } catch (error) {
        // silently fail
      }
    },

    // ---- Preferences (account page + shared edit dialog) ----

    // {name: value} from the API becomes display-ready rows, value as JSON text.
    _prefRows(map) {
      return Object.keys(map || {}).sort().map(name => ({ name, value: JSON.stringify(map[name]) }));
    },

    async loadPreferences() {
      try {
        const response = await this.apiFetch(`${env.apiUrl}preferences`);
        if (response.ok) {
          this.preferences = this._prefRows(await response.json());
          this.preferencesLoaded = true;
        }
      } catch (error) {
        // table shows once a retry succeeds
      }
    },

    // Opens the shared edit dialog. No pref = add; adminMode = an admin
    // editing userPrefsUser (whose row's Preferences button opened the
    // dialog behind) instead of their own account.
    openPrefDialog(pref, adminMode) {
      this.prefEditMode = !!pref;
      this.prefAdminMode = !!adminMode;
      this.prefForm = pref ? { name: pref.name, value: pref.value } : { name: '', value: '' };
      this.update();
      this.querySelector('.preference-dialog').showModal();
    },

    closePrefDialog() {
      this.querySelector('.preference-dialog').close();
    },

    prefDialogTitle() {
      const title = this.prefEditMode ? 'Edit Preference' : 'Add Preference';
      return this.prefAdminMode ? title + ' — ' + (this.userPrefsUser.email || '') : title;
    },

    // The value field is JSON; bare text that doesn't parse is saved as a
    // string, so "dark" and dark mean the same thing.
    _prefParseValue(text) {
      try {
        return JSON.parse(text);
      } catch (error) {
        return text;
      }
    },

    async savePref() {
      const name = this.prefForm.name.trim();
      if (!name) {
        this.showToast('Please enter a preference name', 'danger');
        return;
      }
      const value = this._prefParseValue(this.prefForm.value.trim());
      const admin = this.prefAdminMode;
      this.prefSaving = true;
      this.update();
      try {
        const response = await this.apiFetch(`${env.apiUrl}${admin ? 'admin/user/preference' : 'preference'}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(admin ? { user_id: this.userPrefsUser.id, name, value } : { name, value }),
        });
        const msg = await response.json();
        if (response.ok) {
          this.closePrefDialog();
          this.showToast(msg);
          await (admin ? this.loadUserPrefs() : this.loadPreferences());
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (error) {
        this.showToast(error.message, 'danger');
      } finally {
        this.prefSaving = false;
      }
    },

    async deletePref(pref) {
      const confirmed = await this.showConfirm({
        title: 'Delete Preference',
        message: `Delete the preference "${pref.name}"? Apps that saved it will fall back to their defaults.`,
        action: 'Delete',
        danger: true,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(`${env.apiUrl}preference?name=${encodeURIComponent(pref.name)}`, { method: 'DELETE' });
        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          await this.loadPreferences();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (error) {
        this.showToast(error.message, 'danger');
      }
    },

    async saveProfile() {
      this.profileLoading = true;
      this.update();
      try {
        const response = await this.apiFetch(`${env.apiUrl}profile`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: this.userName, display_name: this.userDisplayName })
        });

        const data = await response.json();
        if (response.ok) {
          this.userName = data.name || '';
          this.userDisplayName = data.display_name || '';
          this.showToast('Profile updated');
        } else {
          this.showToast(data, 'danger');
        }
      } catch (error) {
        this.showToast(error.message, 'danger');
      } finally {
        this.profileLoading = false;
      }
    },

    async deleteCredential(credId) {
      const confirmed = await this.showConfirm({
        title: 'Delete Passkey',
        message: 'Are you sure you want to delete this passkey? You will no longer be able to use it to sign in.',
        action: 'Delete',
        danger: true,
      });
      if (!confirmed) return;

      try {
        const response = await this.apiFetch(`${env.apiUrl}credentials?id=${encodeURIComponent(credId)}`, {
          method: 'DELETE',
        });

        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          await this.loadCredentials();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (error) {
        this.showToast(error.message, 'danger');
      }
    },

    // ---- SMS sign-in (profile card) ----

    // What this DEPLOYMENT is — SMS sign-in, and whether it is a test copy.
    // Fetched once per page load (deployment config, not account state).
    async loadFeatures() {
      if (this._featuresLoaded) return;
      try {
        const response = await fetch(`${env.pubApiUrl}features`);
        if (response.ok) {
          const features = await response.json();
          this.smsLogin = !!features.sms_login;
          this.testEnv = !!features.test_env;
          this._featuresLoaded = true;
          this.update();
        }
      } catch (error) {
        // card stays hidden
      }
    },

    // ---- test environment ----

    // Asked once per SIGN-IN (sessionStorage, so a new tab or a fresh session
    // asks again), and answerable only by the button: a native dialog closes
    // on Escape unless the cancel event is refused, and an acknowledgement
    // that Escape can dismiss is not an acknowledgement. Returns whether it
    // took over the screen, so the welcome flow waits its turn instead of
    // stacking a second modal on top.
    maybeShowTestEnv() {
      if (!this.testEnv || sessionStorage.getItem('testEnvAck')) return false;
      this.testEnvHost = location.host;
      this.update();
      const dialog = this.querySelector('.testenv-dialog');
      if (!dialog) return false;
      dialog.addEventListener('cancel', (e) => e.preventDefault(), { once: false });
      dialog.showModal();
      return true;
    },

    ackTestEnv() {
      sessionStorage.setItem('testEnvAck', '1');
      this.querySelector('.testenv-dialog')?.close();
      // Deferred until now so two modals never share the screen.
      this.maybeShowWelcome();
    },

    async startPhoneVerify() {
      const phone = (this.phoneInput || '').trim();
      if (!phone) {
        this.showToast('Please enter your mobile number', 'danger');
        return;
      }
      if (!this.phoneConsent) {
        this.showToast('Please agree to receive SMS codes first', 'danger');
        return;
      }
      this.phoneLoading = true;
      this.update();
      try {
        const response = await this.apiFetch(`${env.apiUrl}phone/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone, consent: true })
        });
        const msg = await response.json();
        if (response.ok) {
          this.phoneCodeSent = true;
          this.phoneCode = '';
          this.showToast(msg);
          this.update();
          this.querySelector('#sms-code-input')?.focus();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (error) {
        this.showToast(error.message, 'danger');
      } finally {
        this.phoneLoading = false;
      }
    },

    // Formats like the login code field and submits itself on the 8th char.
    // Texted codes are numeric-only (that's what OS autofill offers).
    onPhoneCodeInput(event) {
      const cleaned = event.target.value.replace(/[^0-9]/g, '').slice(0, 8);
      const formatted = cleaned.length > 4 ? cleaned.slice(0, 4) + '-' + cleaned.slice(4) : cleaned;
      event.target.value = formatted;
      this.phoneCode = formatted;
      if (cleaned.length === 8) {
        this.verifyPhone();
      }
    },

    async verifyPhone() {
      if (this.phoneVerifyLoading || !this.phoneCode.trim()) return;
      this.phoneVerifyLoading = true;
      this.update();
      try {
        const response = await this.apiFetch(`${env.apiUrl}phone/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: this.phoneCode.trim() })
        });
        const data = await response.json();
        if (response.ok) {
          this.phone = data.phone;
          this.phoneVerified = data.phone_verified;
          this.phoneOptedOut = false;
          this.phoneCodeSent = false;
          this.phoneCode = '';
          this.phoneInput = '';
          this.phoneConsent = false;
          this.showToast('Mobile number verified');
        } else {
          this.showToast(data, 'danger');
        }
      } catch (error) {
        this.showToast(error.message, 'danger');
      } finally {
        this.phoneVerifyLoading = false;
      }
    },

    cancelPhoneVerify() {
      this.phoneCodeSent = false;
      this.phoneCode = '';
    },

    async removePhone() {
      const confirmed = await this.showConfirm({
        title: 'Remove mobile number',
        message: 'Remove ' + this.phone + ' from your account? SMS sign-in stops immediately; you can add a number again any time.',
        action: 'Remove',
        danger: true,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(`${env.apiUrl}phone`, { method: 'DELETE' });
        const msg = await response.json();
        if (response.ok) {
          this.phone = '';
          this.phoneVerified = '';
          this.phoneOptedOut = false;
          this.showToast(msg);
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (error) {
        this.showToast(error.message, 'danger');
      }
    },

    async registerPasskey() {
      this.registerLoading = true;
      this.update();
      try {
        const response = await this.apiFetch(`${env.pubApiUrl}passkey_register_start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: this.email })
        });

        if (!response.ok) {
          const msg = await response.json();
          throw new Error('Failed to get registration options: ' + msg);
        }

        const registerSid = response.headers.get('register_sid');
        if (!registerSid) {
          throw new Error('No register_sid in response header');
        }

        const options = await response.json();

        const attestationResponse = await SimpleWebAuthnBrowser.startRegistration({ optionsJSON: options.publicKey });
        const verificationResponse = await this.apiFetch(`${env.pubApiUrl}passkey_register_finish`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'register_sid': registerSid },
          body: JSON.stringify(attestationResponse)
        });

        const result = await verificationResponse.json();
        if (!verificationResponse.ok) {
          this.showToast(result, 'danger');
        } else {
          this.showToast(result.message || result);
          await this.loadCredentials();
        }
      } catch (error) {
        this.showToast(error.message, 'danger');
      } finally {
        this.registerLoading = false;
      }
    },
};
